import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { AbstractControl, UntypedFormArray, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { forkJoin, Subscription } from 'rxjs';
import { CustomValidator, HTS_MASK_6 } from '@helpers/custom-validator';
import { CodeRepository } from '@data-layer/repositories';
import { AddressModel, CountryModel } from '@data-layer/models/common';

@Component({
  selector: 'app-hts-mf',
  templateUrl: './hts-mf.component.html',
  styleUrls: ['./hts-mf.component.scss'],
})
export class HtsMFComponent implements OnInit, OnDestroy {
  @Input() public manufacturerFormGroup: UntypedFormGroup;
  @Input() public index: number;
  @Input() public isDesktop: boolean;
  @Input() public disableMFEdit: boolean;
  @Output() public editMF: EventEmitter<void> = new EventEmitter<void>();

  public mName: string = null;
  public mAddress: AddressModel = null;
  public mID: number = null;
  public HTS_MASK: string = HTS_MASK_6;
  public initialCountries: CountryModel[];
  public countryErrors = {
    required: 'FORM_ERROR.REQUIRED',
  };

  private htsFromArray: UntypedFormArray;
  private subscription = new Subscription();

  constructor(private formBuilder: UntypedFormBuilder, private codeRepository: CodeRepository, private cd: ChangeDetectorRef) {}

  public ngOnInit(): void {
    this.mName = this.manufacturerFormGroup.value.mName;
    this.mID = +this.manufacturerFormGroup.value.mID;
    this.mAddress = this.manufacturerFormGroup.value.mAddress;
    this.htsFromArray = <UntypedFormArray>this.manufacturerFormGroup.get('hts');

    this.checkHTSNumberControls();

    this.subscription.add(
      this.htsFromArray.valueChanges.subscribe((val) => {
        this.checkHTSNumberControls();
      }),
    );

    if (!this.htsFromArray.controls.length) {
      this.addHts();
    }
    this.fillCountries();
  }

  public ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  public get controls(): AbstractControl[] {
    return this.htsFromArray ? this.htsFromArray.controls : [];
  }

  public searchCountry() {
    return this.codeRepository.searchCountry.bind(this.codeRepository);
  }

  private fillCountries() {
    this.subscription.add(
      forkJoin(
        this.htsFromArray.controls
          .map((control) => control.get('countryOfOrigin'))
          .filter((country) => country.value)
          .map((country) => this.codeRepository.getCountry(country.value)),
      ).subscribe((countries) => {
        this.initialCountries = [...countries];
        this.cd.detectChanges();
      }),
    );
  }

  private checkHTSNumberControls() {
    const emptyHts = this.htsFromArray.value.filter((entityHts) => !entityHts.number && !entityHts.countryOfOriginId);
    const emptyHtsCount = emptyHts.length;
    if (emptyHtsCount === 1) {
      return;
    }
    if (!emptyHtsCount) {
      this.addHts();
      return;
    }
    this.htsFromArray.value.forEach((entityHts, index: number) => {
      if (!entityHts.number && !entityHts.countryOfOriginId) {
        this.removeName(index);
      }
    });
  }

  private removeName(index: number): void {
    this.htsFromArray.removeAt(index);
  }

  private addHts() {
    (<UntypedFormArray>this.manufacturerFormGroup.get('hts')).push(this.createEmptyHtsGroup());
  }

  private createEmptyHtsGroup(): UntypedFormGroup {
    return this.formBuilder.group({
      id: this.formBuilder.control(0),
      number: this.formBuilder.control(null, { updateOn: 'blur' }),
      countryOfOrigin: this.formBuilder.control(null),
      countryOfOriginId: this.formBuilder.control(null, CustomValidator.requiredHTSCountryOfOrigin('number')),
      isfEntityId: this.formBuilder.control(this.mID),
    });
  }
}
